import { ComponentProps } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Box, Grid, Loading, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import { PerformanceReviewEvaluationDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { TargetScoreCard } from 'modules/performance_process/components/common/TargetScoreCard'
import { anonymousForTargetBanner } from 'modules/performance_process/components/common/banners'
import { EvaluationAnswer } from 'modules/performance_process/components/review/answer'
import { EstimatedScore } from 'modules/performance_process/components/review/answer/EstimatedScore'
import { From, completedReviewsUrl } from 'modules/performance_process/lib/urls'
import { useAnswers } from 'modules/performance_process/lib/useAnswers'

const EvaluationAnswerPage = () => {
  const { access, company } = useCurrentSession()
  const { evaluationId, targetId } = useParams()
  const { openDialog } = useConfirmationDialog()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const location = useLocation()

  const isRepublishModeFromNavigation = location.state?.isRepublishing === true

  const { data, loading, error } = useQuery(PerformanceReviewEvaluationDocument, {
    variables: { id: Number(evaluationId), companyId: Number(company.id) },
    fetchPolicy: 'network-only',
  })

  const evaluation = data?.performance.reviewEvaluation
  const canAnswer = evaluation?.answeredQuestionnaires?._policies?.answer?.can ?? false

  const ratingAverageInEvaluationFeatureEnabled =
    data?.features.featuresConnection.nodes.find(
      (node) => node.name === 'performance_show_rating_average_in_evaluation'
    )?.enabled ?? false

  const questionnaireContent =
    evaluation?.answeredQuestionnaires?.answeredQuestionnaireWithSections?.content ?? []
  const employeeScoreQuestionnaireContent =
    evaluation?.answeredQuestionnaires?.answeredEmployeeScoreQuestionnaire?.content ?? []
  const employeePotentialScoreQuestionnaireContent =
    evaluation?.answeredQuestionnaires?.answeredEmployeePotentialScoreQuestionnaire?.content ?? []
  const competenciesQuestionnaireContent =
    evaluation?.answeredQuestionnaires?.answeredCompetenciesQuestionnaire?.content ?? []

  const isAlreadyPublished = evaluation?.status === 'published'

  const canRepublish = evaluation?.answeredQuestionnaires?._policies?.republish?.can ?? false
  const isRepublishing = isAlreadyPublished && canRepublish && isRepublishModeFromNavigation
  const evaluationCanEstimateScore =
    evaluation?.answeredQuestionnaires?._policies?.estimateScore?.can ?? false

  const {
    updateAnswer,
    submitPublishAnswer,
    submitRepublishAnswer,
    submittingDraft,
    submittingEstimateScore,
    submittingAnswer,
    submittingRepublish,
    draftSavedAt,
    draftDirty,
    hasAttemptedSubmit,
  } = useAnswers({
    evaluation,
    questionnaireContent,
    competenciesQuestionnaireContent,
    employeeScoreQuestionnaireContent,
    employeePotentialScoreQuestionnaireContent,
    estimateWeightedScore: evaluationCanEstimateScore,
    autosave: !isAlreadyPublished,
    onAnswer: (reviewId) => {
      if (location.pathname.includes('my_results')) {
        navigate(navigation.performanceProcess.reviews.review.fullPath({ reviewId: reviewId }))
      } else if (location.pathname.includes('target') && targetId) {
        navigate(
          navigation.performanceProcess.reviews.review.target.fullPath({
            reviewId: reviewId,
            targetId: targetId,
          })
        )
      } else {
        navigate(completedReviewsUrl(navigation, reviewId, From.EvaluationCompletion))
      }
    },
  })

  if (!evaluation || loading) return <Loading />

  const userIsTarget = evaluation.targetAccess?.id === access.id

  const isSubmitting = submittingAnswer || submittingRepublish

  const reviewIsFinished = evaluation.performanceReviewProcess?.status === 'finished'
  const readOnly = reviewIsFinished || (isAlreadyPublished && !isRepublishing)
  const hasRepublishSetting =
    evaluation.performanceReviewProcess?.republishEvaluationsConfiguration?.enabled ?? false

  const getConfirmationDialogTitle = () => {
    if (isRepublishing) {
      if (userIsTarget) {
        return i18n.t(
          'performance_process.reviews.answer_evaluation.confirmation_modal.title_self_review_republish'
        )
      }

      return i18n.t(
        'performance_process.reviews.answer_evaluation.confirmation_modal.title_with_target_name_republish',
        { targetName: evaluation.targetAccess?.firstName || '' }
      )
    }

    if (userIsTarget) {
      return i18n.t(
        'performance_process.reviews.answer_evaluation.confirmation_modal.title_self_review'
      )
    }

    return i18n.t(
      'performance_process.reviews.answer_evaluation.confirmation_modal.title_with_target_name',
      { targetName: evaluation.targetAccess?.firstName || '' }
    )
  }

  const getConfirmationDescription = () => {
    if (isRepublishing) {
      return i18n.t(
        'performance_process.reviews.answer_evaluation.confirmation_modal.description_republish'
      )
    }

    if (hasRepublishSetting) {
      return i18n.t(
        'performance_process.reviews.answer_evaluation.confirmation_modal.description_republishable'
      )
    }

    return i18n.t('performance_process.reviews.answer_evaluation.confirmation_modal.description')
  }

  const handleSubmit = async () => {
    openDialog({
      title: getConfirmationDialogTitle(),
      description: getConfirmationDescription(),
      confirmLabel: i18n.t('performance_process.reviews.answer_evaluation.actions.publish'),
      cancelLabel: isRepublishing ? i18n.t('common.cancel') : undefined,
      onConfirm: () => (isRepublishing ? submitRepublishAnswer() : submitPublishAnswer()),
    })
  }

  const getDraftStatus = () => {
    if (isAlreadyPublished) return undefined

    if (draftDirty) {
      return {
        text: i18n.t('performance_process.reviews.answer_evaluation.actions.saving'),
        loading: true,
      }
    }

    if (draftSavedAt) {
      return {
        icon: 'check' as const,
        text: i18n.t('performance_process.reviews.answer_evaluation.actions.saved_as_draft'),
      }
    }

    return undefined
  }

  const transactionalActions: ComponentProps<typeof ResponsivePage.Body>['transactionalActions'] =
    canAnswer && (!isAlreadyPublished || isRepublishing)
      ? {
          primary: {
            label: i18n.t('performance_process.reviews.answer_evaluation.actions.publish'),
            onClick: () => handleSubmit(),
            disabled: isSubmitting || (isRepublishing && !draftDirty),
          },
          secondary: isRepublishing
            ? {
                label: i18n.t('common.cancel'),
                onClick: () => navigate(-1),
              }
            : undefined,
          status: getDraftStatus(),
        }
      : undefined

  const showBannerReviewerAnonymousForTarget =
    !userIsTarget &&
    evaluation.evaluationType === 'peers' &&
    evaluation.performanceReviewProcess?.visibilitySetting?.anonymousPeerEvaluationForTarget

  const evaluationScore = evaluation.evaluationScore

  return (
    <ResponsivePage.Body
      transactionalActions={canAnswer ? transactionalActions : undefined}
      loading={loading}
      banner={showBannerReviewerAnonymousForTarget ? anonymousForTargetBanner() : undefined}
      error={error}
    >
      <Box gap="s24">
        {isRepublishing && (
          <OneAlert
            title={i18n.t(
              'performance_process.reviews.answer_evaluation.editing_published_alert.title'
            )}
            description={i18n.t(
              'performance_process.reviews.answer_evaluation.editing_published_alert.description'
            )}
            variant="warning"
          />
        )}

        {evaluationScore &&
          evaluationScore?.score &&
          evaluationScore?.scaleMax &&
          !isRepublishing && (
            <Grid variant="fixed">
              <TargetScoreCard
                title={i18n.t('performance_process.targets.insights.performance_score')}
                score={evaluationScore?.score}
                scaleMax={evaluationScore?.scaleMax}
              />
            </Grid>
          )}

        {evaluation && (
          <EvaluationAnswer
            evaluation={evaluation}
            ratingAverageInEvaluationFeatureEnabled={ratingAverageInEvaluationFeatureEnabled}
            onUpdateAnswer={updateAnswer}
            hasAttemptedSubmit={hasAttemptedSubmit}
            readOnly={readOnly}
          />
        )}

        {evaluationCanEstimateScore && !readOnly && (
          <Box gap="s24" gridColSpan={{ xxs: 10 }}>
            <EstimatedScore
              estimatedScore={evaluation.answeredQuestionnaires?.estimatedScore ?? null}
              submittingEstimateScore={submittingDraft || submittingEstimateScore}
            />
          </Box>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default EvaluationAnswerPage
